import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import ParticleBackground from '../components/ParticleBackground';
import { ArrowRight, Mic2, Music2, Sliders } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const Home = () => {
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "MusicRecordingStudio",
    "name": "Jeanjo Unis Studio",
    "image": "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04", 
    "url": "https://jeanjo-unis-studio.com",
    "telephone": "+33663156144",
    "email": "asso.jeanjo.unis@gmail.com",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "18 avenue des arnavaux",
      "addressLocality": "Marseille",
      "postalCode": "13014",
      "addressCountry": "FR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 43.3283, 
      "longitude": 5.3855
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        "opens": "10:00",
        "closes": "22:00"
      }
    ],
    "priceRange": "$$"
  };

  return (
    <div className="relative min-h-screen">
      <Helmet>
        <title>Studio d'Enregistrement Marseille | Jeanjo Unis Studio</title>
        <meta name="description" content="Jeanjo Unis Studio : Le meilleur studio d'enregistrement à Marseille (13014). Enregistrement voix (Rap/Pop), Mixage, Mastering et Beatmaking pro. Tarifs attractifs et équipement haut de gamme." />
        <meta name="keywords" content="studio enregistrement marseille, studio rap marseille, mixage mastering marseille, studio musique 13014, prix studio enregistrement marseille, beatmaker marseille, studio enregistrement pas cher marseille" />
        <link rel="canonical" href="https://jeanjo-unis-studio.com/" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <ParticleBackground />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-neutral-950/50 to-neutral-950 pointer-events-none" />
        
        <div className="relative z-10 text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-purple-400 font-medium tracking-widest text-sm uppercase mb-4">
              Studio d'enregistrement Professionnel à Marseille
            </h2>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight leading-tight">
              Jeanjo Unis <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-blue-500">Studio</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Le lieu de création incontournable à Marseille. Enregistrement, Mixage, Mastering et Beatmaking au 18 avenue des arnavaux.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/booking">
                <button className="px-8 py-4 bg-white text-black font-bold rounded-full hover:bg-gray-200 transition-all flex items-center gap-2 group">
                  Réserver une session
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
              <Link to="/beats">
                <button className="px-8 py-4 bg-white/10 backdrop-blur-md border border-white/20 text-white font-bold rounded-full hover:bg-white/20 transition-all">
                  Écouter nos Beats
                </button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-4 bg-neutral-950">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Nos Services</h2>
            <div className="w-20 h-1 bg-purple-500 mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link to="/booking" className="block h-full">
              <ServiceCard 
                icon={<Mic2 className="w-10 h-10 text-purple-400" />}
                title="Enregistrement"
                desc="Cabine insonorisée, micros Neumann et préamplis haut de gamme pour une prise de voix parfaite."
                img="https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?auto=format&fit=crop&w=800&q=80"
              />
            </Link>
            <Link to="/booking" className="block h-full">
              <ServiceCard 
                icon={<Sliders className="w-10 h-10 text-blue-400" />}
                title="Mixage & Mastering"
                desc="Donnez à vos titres la qualité radio qu'ils méritent. Équilibre, clarté et puissance."
                img="https://images.unsplash.com/photo-1598653222000-6b7b7a552625?auto=format&fit=crop&w=800&q=80"
              />
            </Link>
            <Link to="/beats" className="block h-full">
              <ServiceCard 
                icon={<Music2 className="w-10 h-10 text-pink-400" />}
                title="Beatmaking"
                desc="Des instrumentales sur mesure ou sur catalogue. Trap, Drill, Afro, Pop."
                img="https://images.unsplash.com/photo-1570134505188-288da1aa64ee?auto=format&fit=crop&w=800&q=80"
              />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const ServiceCard = ({ icon, title, desc, img }) => (
  <motion.div 
    whileHover={{ y: -10 }}
    className="group relative overflow-hidden rounded-2xl bg-neutral-900 border border-white/10 h-full flex flex-col"
  >
    <div className="aspect-video overflow-hidden shrink-0">
      <img src={img} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 opacity-60 group-hover:opacity-40" />
    </div>
    <div className="p-8 relative z-10 -mt-10 flex-1 flex flex-col">
      <div className="w-16 h-16 bg-neutral-950 rounded-2xl flex items-center justify-center border border-white/10 shadow-xl mb-6">
        {icon}
      </div>
      <h3 className="text-2xl font-bold mb-3">{title}</h3>
      <p className="text-gray-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </motion.div>
);

export default Home;
